import { normalizeNewsItem, normalizeUtf8Text, resolveMediaUrl, routes } from "../data/noticias";
import { resolveAdminCategoryValue } from "./config";

function buildAdminApiUrl(path, server = routes.Vercel) {
    const base = String(server || routes.Vercel).replace(/\/+$/, "");
    const cleanPath = String(path || "").replace(/^\/+/, "");

    return `${base}/${cleanPath}`;
}

async function fetchJson(url, options = {}) {
    const response = await fetch(url, options);

    if (!response.ok) {
        throw new Error(`No se pudo obtener ${url}`);
    }

    return response.json();
}

function sortRawNews(items = []) {
    return [...items].sort((leftItem, rightItem) => {
        const leftTime = new Date(leftItem?.fecha_habilitacion || leftItem?.fecha || 0).getTime();
        const rightTime = new Date(rightItem?.fecha_habilitacion || rightItem?.fecha || 0).getTime();

        if (leftTime !== rightTime) {
            return rightTime - leftTime;
        }

        return Number(rightItem?.id || 0) - Number(leftItem?.id || 0);
    });
}

function sortRawNewsAscending(items = []) {
    return [...items].sort((leftItem, rightItem) => {
        const leftTime = new Date(leftItem?.fecha_habilitacion || leftItem?.fecha || 0).getTime();
        const rightTime = new Date(rightItem?.fecha_habilitacion || rightItem?.fecha || 0).getTime();

        if (leftTime !== rightTime) {
            return leftTime - rightTime;
        }

        return Number(leftItem?.id || 0) - Number(rightItem?.id || 0);
    });
}

export function buildAdminNewsSlug(title) {
    return normalizeUtf8Text(title)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export async function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export function formatAdminPublishDate(dateValue, timeValue) {
    const safeTime = timeValue || "00:00";
    return new Date(`${dateValue}T${safeTime}:00-03:00`).toISOString();
}

export function extractAdminDateInputs(value) {
    if (!value) {
        return { date: "", time: "" };
    }

    const date = new Date(String(value).replace(" ", "T"));

    if (Number.isNaN(date.getTime())) {
        return { date: "", time: "" };
    }

    const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Argentina/Buenos_Aires",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    });

    const parts = formatter.formatToParts(date);
    const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return {
        date: `${partMap.year}-${partMap.month}-${partMap.day}`,
        time: `${partMap.hour}:${partMap.minute}`,
    };
}

export function createEmptyAdminDraft() {
    return {
        id: null,
        title: "",
        slug: "",
        description: "",
        category: "",
        image: "",
        coverImage: "",
        videoUrl: "",
        body: "",
        publishDate: "",
        publishTime: "",
        related1: "",
        related2: "",
        related3: "",
    };
}

export function mapAdminNoteToDraft(note) {
    const { date, time } = extractAdminDateInputs(note?.fecha_habilitacion || note?.fecha || "");
    const rawCategory = normalizeUtf8Text(note?.categoria || "").trim();

    return {
        id: note?.id ?? null,
        title: normalizeUtf8Text(note?.titulo || "").trim(),
        slug: normalizeUtf8Text(note?.url || "").trim() || buildAdminNewsSlug(note?.titulo || ""),
        description: normalizeUtf8Text(note?.descripcion || "").trim(),
        category: resolveAdminCategoryValue(rawCategory) || rawCategory,
        image: normalizeUtf8Text(note?.imagen || "").trim(),
        coverImage: normalizeUtf8Text(note?.imagen_portada || "").trim(),
        videoUrl: normalizeUtf8Text(note?.link_video || "").trim(),
        body: normalizeUtf8Text(note?.cuerpo || ""),
        publishDate: date,
        publishTime: time,
        related1: normalizeUtf8Text(note?.art_rel_1 || "").trim(),
        related2: normalizeUtf8Text(note?.art_rel_2 || "").trim(),
        related3: normalizeUtf8Text(note?.art_rel_3 || "").trim(),
    };
}

export async function fetchAdminHomeMainNotes(server, signal) {
    const result = await fetchJson(buildAdminApiUrl("/home-dto-main", server), { signal });
    const safeItems = Array.isArray(result) ? [...result] : [];

    return safeItems
        .map((item, index) => {
            const title = normalizeUtf8Text(item?.titulo || "").trim();
            const rawOrder = Number(item?.orden_home ?? item?.ordenHome);
            const orderHome = Number.isFinite(rawOrder) && rawOrder > 0 ? rawOrder : index + 1;

            return {
                id: item?.id ?? null,
                title,
                slug: normalizeUtf8Text(item?.url || "").trim() || buildAdminNewsSlug(title),
                image: resolveMediaUrl(item?.imagen, server),
                orderHome,
            };
        })
        .filter((item) => item.id && item.title && item.slug)
        .sort((leftItem, rightItem) => leftItem.orderHome - rightItem.orderHome)
        .slice(0, 6);
}

export async function fetchAdminNoteOptions(server, signal) {
    const result = await fetchJson(buildAdminApiUrl("/noticias-todo-dto-1", server), { signal });
    const safeItems = Array.isArray(result) ? sortRawNews(result) : [];
    const mappedOptions = safeItems
        .map((item) => {
            const title = normalizeUtf8Text(item?.titulo || "").trim();

            return {
                id: item?.id ?? null,
                title,
            };
        })
        .filter((item) => item.title);

    return mappedOptions;
}

export async function fetchScheduledAdminNoteOptions(server, signal) {
    const result = await fetchJson(buildAdminApiUrl("/noticias-programadas-dto-1", server), { signal });
    const safeItems = Array.isArray(result) ? sortRawNewsAscending(result) : [];

    return safeItems
        .map((item) => {
            const title = normalizeUtf8Text(item?.titulo || "").trim();

            return {
                id: item?.id ?? null,
                title,
            };
        })
        .filter((item) => item.title);
}

export async function fetchAllAdminNotes(server, signal) {
    const result = await fetchJson(buildAdminApiUrl("/noticias-todo", server), { signal });
    return Array.isArray(result) ? sortRawNews(result) : [];
}

export async function fetchScheduledAdminNotes(server, signal) {
    const result = await fetchJson(buildAdminApiUrl("/noticias-programadas", server), { signal });
    return Array.isArray(result) ? sortRawNews(result) : [];
}

export async function fetchScheduledAdminPreviewNotes(server, signal) {
    const result = await fetchJson(buildAdminApiUrl("/noticias-programadas-dto-2", server), { signal });
    const safeItems = Array.isArray(result) ? sortRawNewsAscending(result) : [];

    return safeItems
        .filter((item) => item?.titulo)
        .map((item) => {
            const title = normalizeUtf8Text(item?.titulo || "").trim();

            return {
                id: item?.id ?? null,
                title,
                slug: buildAdminNewsSlug(title),
                image: resolveMediaUrl(item?.imagen, server),
            };
        })
        .filter((item) => item.title && item.slug);
}

function resolveAdminViewsValue(item) {
    const rawValue = [
        item?.vistas,
        item?.views,
        item?.cantidad_vistas,
        item?.cant_vistas,
        item?.ranking_vistas,
        item?.visitas,
    ].find((value) => value !== undefined && value !== null && String(value).trim() !== "");

    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : 0;
}

const ADMIN_CATEGORY_COLORS = {
    "desde adentro": "#d73f2f",
    "segunda opinion": "#b2a700",
    "2da opinion": "#b2a700",
    "medicina 5 0": "#0a7e8c",
    "el nudo": "#80623c",
    "inspirando el cambio": "#303754",
    editoriales: "#4b5639",
    datafisp: "#4b5639",
    recetas: "#0a7e8c",
    "en foco": "#0a7e8c",
    "real food": "#fd6e24",
    "vida sana": "#43a5ee",
};

function toAdminCategoryColorKey(value) {
    return normalizeUtf8Text(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, " ")
        .trim()
        .toLowerCase();
}

function resolveAdminCategoryColor(value) {
    return ADMIN_CATEGORY_COLORS[toAdminCategoryColorKey(value)] || "#0a7e8c";
}

function extractAdminCategoryValue(item) {
    const directValue = [
        item?.categoria,
        item?.categoría,
        item?.category,
        item?.seccion,
        item?.sección,
        item?.segmento,
        item?.section,
        item?.cat,
    ].find((value) => value !== undefined && value !== null && String(value).trim() !== "");

    if (directValue !== undefined) {
        return normalizeUtf8Text(String(directValue)).trim();
    }

    const matchingEntry = Object.entries(item || {}).find(([key, value]) => {
        if (value === undefined || value === null || String(value).trim() === "") {
            return false;
        }

        const normalizedKey = normalizeUtf8Text(String(key))
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-zA-Z0-9]+/g, "")
            .toLowerCase();

        return (
            normalizedKey.includes("categoria") ||
            normalizedKey.includes("category") ||
            normalizedKey.includes("seccion") ||
            normalizedKey.includes("segmento") ||
            normalizedKey.includes("section")
        );
    });

    return matchingEntry ? normalizeUtf8Text(String(matchingEntry[1])).trim() : "";
}

function mapAdminLightListItem(item, server) {
    const title = normalizeUtf8Text(item?.titulo || "").trim();
    const slug = normalizeUtf8Text(item?.url || "").trim() || buildAdminNewsSlug(title);
    const rawCategory = extractAdminCategoryValue(item);
    const categoryLabel = rawCategory || "Sin categoría";

    return {
        id: item?.id ?? null,
        title,
        slug,
        image: resolveMediaUrl(item?.imagen, server),
        category: categoryLabel,
        categoryColor: resolveAdminCategoryColor(rawCategory || categoryLabel),
    };
}

export async function fetchAdminMetricsNotes(server, signal, page = 1) {
    const safePage = Math.max(1, Number(page) || 1);
    const primaryUrl = buildAdminApiUrl(`/noticias-views?page=${safePage}`, server);

    try {
        const result = await fetchJson(primaryUrl, { signal });
        const safeItems = Array.isArray(result) ? sortRawNews(result) : [];

        return safeItems
            .map((item) => ({
                ...mapAdminLightListItem(item, server),
                views: resolveAdminViewsValue(item),
            }))
            .filter((item) => item.title && item.slug);
    } catch (error) {
        console.warn("[FISP TV Admin] fetchAdminMetricsNotes fallback", {
            server,
            requestUrl: primaryUrl,
            message: error?.message || "unknown error",
        });

        if (safePage > 1) {
            return [];
        }

        const fallback = await fetchJson(buildAdminApiUrl("/noticias-mas-vistas-dto-1", server), { signal });
        const safeItems = Array.isArray(fallback) ? fallback : [];

        return safeItems
            .map((item) => ({
                ...mapAdminLightListItem(item, server),
                views: resolveAdminViewsValue(item),
            }))
            .filter((item) => item.title && item.slug);
    }
}

export async function fetchAdminReplacementNotes(server, signal, page = 1) {
    const safePage = Math.max(1, Number(page) || 1);
    const primaryUrl = buildAdminApiUrl(`/noticias-views?page=${safePage}`, server);

    try {
        const result = await fetchJson(primaryUrl, { signal });
        const safeItems = Array.isArray(result) ? sortRawNews(result) : [];

        return safeItems
            .map((item) => mapAdminLightListItem(item, server))
            .filter((item) => item.title && item.slug);
    } catch (error) {
        console.warn("[FISP TV Admin] fetchAdminReplacementNotes fallback", {
            server,
            requestUrl: primaryUrl,
            message: error?.message || "unknown error",
        });

        const fallback = await fetchJson(buildAdminApiUrl("/noticias-hoy-dto-3", server), { signal });
        const safeItems = Array.isArray(fallback) ? sortRawNews(fallback) : [];
        const startIndex = (safePage - 1) * 20;
        const pageItems = safeItems.slice(startIndex, startIndex + 20);

        return pageItems
            .map((item) => mapAdminLightListItem(item, server))
            .filter((item) => item.title && item.slug);
    }
}

export async function fetchAdminNoteByTitle(title, server, signal) {
    const slug = buildAdminNewsSlug(title);
    const requestUrl = buildAdminApiUrl(`/noticias/${encodeURIComponent(slug)}`, server);
    const result = await fetchJson(requestUrl, { signal });
    const note = Array.isArray(result) ? result[0] : result;

    if (!note) {
        return null;
    }

    return {
        raw: note,
        normalized: normalizeNewsItem(note, server),
    };
}

export async function createAdminNote(payload, server) {
    return fetchJson(buildAdminApiUrl("/noticias", server), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
}

export async function updateAdminNote(payload, server) {
    return fetchJson(buildAdminApiUrl("/noticias", server), {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
}

export async function updateAdminHomeOrder(payload, server) {
    return fetchJson(buildAdminApiUrl("/ordenar-home", server), {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
}
